// OperatorMemoryService.ts — Sprint 093: Operator Intelligence Memory™ (Build 1.86.0)
//
// SINGLE RESPONSIBILITY: Load, save, merge, and query operator memory.
//
// RULES:
//   • Pure TypeScript. No React. No API calls.
//   • localStorage only — same persistence pattern as operatorLearningEngine.ts.
//   • No side effects outside persistence (no window, no CustomEvent).
//   • All public functions are safe to call from any engine or component.
//   • Memory NEVER replaces Intelligence, Trust, or Learning scores. Additive only.
//
// USAGE:
//   import { recordMemoryEvent, getCompanyMemory, getGlobalStats } from '@/services/OperatorMemoryService';

import type {
  CompanyMemory,
  OperatorMemoryStore,
  GlobalMemoryStats,
  MemoryEvent,
} from '@/services/OperatorMemory';

// ─── Storage ──────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'nomad_operator_memory_v1';

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Normalise a company name to a consistent cache/storage key.
 * Lowercase, trimmed, single-space separated.
 */
export function normalizeCompanyKey(name: string | null | undefined): string {
  const s = (name ?? '').toLowerCase().trim().replace(/\s+/g, ' ');
  return s || 'unknown';
}

function emptyCompanyMemory(companyName: string, now: string): CompanyMemory {
  return {
    companyName,
    firstSeen:               now,
    lastSeen:                now,
    timesViewed:             0,
    timesOpened:             0,
    applicationsStarted:     0,
    applicationsSubmitted:   0,
    interviewsReceived:      0,
    offersReceived:          0,
    missionsCompleted:       0,
    tasksCompleted:          0,
    tasksSkipped:            0,
    estimatedEarned:         0,
    actualEarned:            0,
    estimatedHours:          0,
    actualHours:             0,
    averageHourlyRate:       null,
    averagePaymentDelay:     null,
    averageCompletionTime:   null,
    trustAdjustment:         0,
    successRate:             null,
    favorite:                false,
    hidden:                  false,
    notes:                   '',
    lastOutcome:             null,
  };
}

function recalcDerivedFields(m: CompanyMemory): void {
  // averageHourlyRate
  if (m.actualHours > 0 && m.actualEarned > 0) {
    m.averageHourlyRate = Math.round((m.actualEarned / m.actualHours) * 100) / 100;
  }
  // successRate: completed / submitted (not started — submitted = committed)
  if (m.applicationsSubmitted > 0) {
    m.successRate = Math.round((m.missionsCompleted / m.applicationsSubmitted) * 100);
  }
}

function rollingAverage(current: number | null, count: number, newValue: number): number {
  if (current === null || count <= 1) return newValue;
  return Math.round(((current * (count - 1)) + newValue) / count * 10) / 10;
}

// ─── These are the ONLY functions allowed to touch localStorage ───────────────

/**
 * Load the full memory store from localStorage.
 * Returns an empty store if absent or corrupted.
 */
export function loadMemory(): OperatorMemoryStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as OperatorMemoryStore;
      if (parsed.version === 1 && typeof parsed.companies === 'object') {
        return parsed;
      }
    }
  } catch {
    // corrupted storage — return empty
  }
  return { version: 1, companies: {}, lastUpdated: new Date().toISOString() };
}

/**
 * Persist the memory store to localStorage.
 * Silently ignores QuotaExceededError.
 */
export function saveMemory(store: OperatorMemoryStore): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...store, lastUpdated: new Date().toISOString() }),
    );
    // LIVE PLATFORM: announce the mutation (legacy bridge → core bus).
    window.dispatchEvent(new CustomEvent('nomad:data-changed', { detail: { domain: 'memory' } }));
  } catch {
    // localStorage full or unavailable — silently skip
  }
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Record a memory event for a company.
 * Loads the store, applies the update, and saves in one atomic operation.
 * All downstream statistics (successRate, averageHourlyRate) are recomputed on write.
 *
 * @param event — A typed MemoryEvent describing what happened
 */
export function recordMemoryEvent(event: MemoryEvent): void {
  const store = loadMemory();
  const key   = normalizeCompanyKey(event.companyName);
  const now   = new Date().toISOString();

  if (!store.companies[key]) {
    store.companies[key] = emptyCompanyMemory(event.companyName, now);
  }

  const m = store.companies[key];
  m.lastSeen = now;

  switch (event.type) {
    case 'opportunity_viewed':
      m.timesViewed++;
      m.timesOpened++;
      break;

    case 'application_started':
      m.applicationsStarted++;
      if (event.estimatedPayout !== null && event.estimatedPayout > 0) {
        m.estimatedEarned += event.estimatedPayout;
      }
      break;

    case 'application_submitted':
      m.applicationsSubmitted++;
      if (event.estimatedPayout !== null && event.estimatedPayout > 0) {
        // Only add if not already counted at 'started'
        // (If started event was recorded, estimatedEarned already includes this)
        // Guard: only add if applicationsSubmitted > applicationsStarted (unusual path)
        if (m.applicationsSubmitted > m.applicationsStarted) {
          m.estimatedEarned += event.estimatedPayout;
        }
      }
      m.lastOutcome = null; // pending outcome
      break;

    case 'mission_started':
      // No separate counter — tracked via applicationsStarted.
      // Future sprints may differentiate.
      break;

    case 'mission_completed':
      m.missionsCompleted++;
      m.tasksCompleted++;
      m.actualEarned  += event.actualEarned;
      m.actualHours   += event.actualHours;
      m.lastOutcome    = 'completed';
      break;

    case 'payment_recorded': {
      m.actualEarned += event.amount;
      const prevCount = m.missionsCompleted || 1;
      m.averagePaymentDelay = rollingAverage(m.averagePaymentDelay, prevCount, event.delayDays);
      break;
    }

    case 'application_rejected':
      m.lastOutcome = 'rejected';
      break;

    case 'offer_accepted':
      m.offersReceived++;
      m.lastOutcome = 'accepted';
      break;

    case 'task_skipped':
      m.tasksSkipped++;
      m.lastOutcome = 'skipped';
      break;
  }

  recalcDerivedFields(m);
  store.companies[key] = m;
  saveMemory(store);
}

/**
 * Return the memory record for a specific company, or null if no record exists.
 * This is the primary read API — any engine or component calls this.
 *
 * @param companyName — Company display name (any case/whitespace, normalised internally)
 */
export function getCompanyMemory(companyName: string | null | undefined): CompanyMemory | null {
  if (!companyName) return null;
  const store = loadMemory();
  const key   = normalizeCompanyKey(companyName);
  return store.companies[key] ?? null;
}

/**
 * Compute aggregate operator statistics from the current memory store.
 * All returned values are derived; none are persisted separately.
 */
export function getGlobalStats(): GlobalMemoryStats {
  const store     = loadMemory();
  const companies = Object.values(store.companies);

  if (companies.length === 0) {
    return {
      totalCompaniesWorked: 0,
      totalApplications:    0,
      totalMissions:        0,
      totalEarnings:        0,
      averageHourly:        null,
      averageSuccessRate:   null,
      favoriteCompany:      null,
      fastestPayingCompany: null,
      highestPayingCompany: null,
      longestRelationship:  null,
    };
  }

  const totalApplications   = companies.reduce((s, c) => s + c.applicationsSubmitted, 0);
  const totalMissions        = companies.reduce((s, c) => s + c.missionsCompleted, 0);
  const totalEarnings        = companies.reduce((s, c) => s + c.actualEarned, 0);
  const totalHours           = companies.reduce((s, c) => s + c.actualHours, 0);
  const averageHourly        = totalHours > 0
    ? Math.round((totalEarnings / totalHours) * 100) / 100
    : null;

  const withRate        = companies.filter(c => c.successRate !== null);
  const averageSuccessRate = withRate.length > 0
    ? Math.round(withRate.reduce((s, c) => s + (c.successRate ?? 0), 0) / withRate.length)
    : null;

  // Favourite: explicit flag first, then highest timesViewed
  const explicitFav = companies.find(c => c.favorite);
  const favoriteCompany = explicitFav
    ? explicitFav.companyName
    : companies.reduce((best, c) => c.timesViewed > best.timesViewed ? c : best, companies[0]).companyName;

  const withDelay = companies.filter(c => c.averagePaymentDelay !== null);
  const fastestPayingCompany = withDelay.length > 0
    ? withDelay.reduce((best, c) =>
        (c.averagePaymentDelay ?? 9999) < (best.averagePaymentDelay ?? 9999) ? c : best,
      ).companyName
    : null;

  const withEarnings = companies.filter(c => c.actualEarned > 0);
  const highestPayingCompany = withEarnings.length > 0
    ? withEarnings.reduce((best, c) => c.actualEarned > best.actualEarned ? c : best).companyName
    : null;

  const longestRelationship = companies
    .reduce((earliest, c) => c.firstSeen < earliest.firstSeen ? c : earliest)
    .companyName;

  return {
    totalCompaniesWorked: companies.length,
    totalApplications,
    totalMissions,
    totalEarnings,
    averageHourly,
    averageSuccessRate,
    favoriteCompany,
    fastestPayingCompany,
    highestPayingCompany,
    longestRelationship,
  };
}

/**
 * Serialise the full memory store to a pretty-printed JSON string.
 * Use for the OCC Export Memory button.
 */
export function exportMemory(): string {
  return JSON.stringify(loadMemory(), null, 2);
}

/**
 * Parse and import a memory JSON string (from a previous exportMemory() call).
 * Returns true on success, false if the JSON is invalid or has the wrong schema.
 */
export function importMemory(json: string): boolean {
  try {
    const parsed = JSON.parse(json) as OperatorMemoryStore;
    if (parsed.version === 1 && typeof parsed.companies === 'object') {
      saveMemory(parsed);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * Permanently delete all operator memory from localStorage.
 * Irreversible — the caller is responsible for showing a confirmation prompt.
 */
export function resetMemory(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // unavailable — silently skip
  }
}
